import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Archivo } from "@models/archivo.model";
import { ErrorService } from "@services/error.service";
import { WarningError } from "@shared/errors/custom.error";
import { assignToModel } from "@shared/utils/fieldsMap.utils";

@Component({
	selector: "app-upload-document",
	templateUrl: "./upload.component.html",
	styleUrls: ["./upload.component.scss"],
})
export class UploadDocumentComponent {
	@Input() titulo: string; // Título del componente que se muestra
	@Input() tituloModal: string = "Enviar solicitud de Prórroga"; // Titulo del modal que abre el componente
	@Input() tamañoMax: number = 25; // Tamaño máximo del documento (en mb)
	@Input() tiposPermitidos: string[] = [
		"application/pdf",
		"image/png",
		"image/jpeg",
	]; // Documentos permitidos
	@Input() verPreview: boolean = true; // Si se muestra el boton de vista previa
	@Input() mostrarPlantilla: boolean = false; // Si se muestra el boton de la plantilla del documento que se debe subir
	@Input() plantillaUrl: string; // Url de la plantilla que se descargara el documento
	@Input() botonGuardar: { label: string; title: string }; // Información que mostrara el boton de guardarDocumento
	@Input() conPadding: boolean = true; // Contenedor con padding
	@Input() descripcion: string =
		"Documento en formato PDF, PNG o JPG (Tamaño máximo 25MB)"; // Texto que se muestra sobre el boton cargar

	@Output() retornarDocumento = new EventEmitter<Archivo>(); // Retorna el documento subido al componente padre
	@Output() urlTemporal = new EventEmitter<SafeResourceUrl>(); // Retorna ñaurl temporal del documento para mostrar la vista previa en el componente padre
	@Output() btnGuardar = new EventEmitter(); // Retorna el click del boton guardar para que el componente padre guarde el documento
	@ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

	constructor(
		private errorService: ErrorService,
		private sanitizer: DomSanitizer,
	) {}

	documentoSeleccionado: File; // Variable que guarda el documento seleccionado
	mostrarModal: boolean = false; // Variable que maneja el renderizado del modal
	documentoUrl: SafeResourceUrl; // Url del documento almacenado en menoria

	get url() {
		return URL.createObjectURL(this.documentoSeleccionado);
	}

	get isPDF() {
		return this.documentoSeleccionado?.type === "application/pdf";
	}

	obtenerUrlTemporal() {
		this.documentoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
	}

	seleccionarArchivo() {
		this.fileInput.nativeElement.click();
	}

	abrirPreview() {
		this.mostrarModal = true;
	}

	/**
	 * Guarda en memoria el documento y devuelve la información al componente padre
	 */
	async handleSeleccionarArchivo(event: Event) {
		try {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];

			if (!file) {
				this.documentoSeleccionado = undefined;
				this.retornarDocumento.emit();
				return;
			}

			this.validarFormatoDocumento(file);
			this.validarTamañoDocumento(file);

			const { base64 } = await this.obtenerBase64(file);

			const a = {
				nombre: file.name,
				base64: base64,
				mime: file.type,
			};
			const archivo = assignToModel(Archivo, a);
			this.documentoSeleccionado = file;
			this.obtenerUrlTemporal();
			this.retornarDocumento.emit(archivo);
			this.urlTemporal.emit(this.documentoUrl);
		} catch (error) {
			this.errorService.handle(error, "Error al seleccionar el documento");
		}
	}

	obtenerBase64(file: File): Promise<{ base64: string; encabezado: string }> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = () => {
				const resultado = reader.result as string;
				const [encabezado, base64] = resultado.split(",");
				resolve({ base64, encabezado });
			};

			reader.onerror = (error) => reject(error);

			reader.readAsDataURL(file);
		});
	}

	validarTamañoDocumento(file: File) {
		const maxSizeBytes = this.tamañoMax * 1024 * 1024;

		if (file.size > maxSizeBytes) {
			this.fileInput.nativeElement.value = ""; // limpiar el input
			throw new WarningError(
				"El documento seleccionado supera el máximo permitido",
			);
		}
	}

	validarFormatoDocumento(file: File) {
		if (!this.tiposPermitidos.includes(file.type)) {
			this.fileInput.nativeElement.value = "";
			throw new WarningError(
				"Debe seleccionar un documento en uno de los formatos permitidos",
			);
		}
	}

	abrirPlantilla(ruta: string) {
		window.open(ruta, "_blank");
	}

	/*
	 * Avisa al componente padre que debe guardar el documento
	 */
	guardarDocumento() {
		this.btnGuardar.emit();
	}

	/*
	 * Limpia el documento cuando se llama la función desde el componente padre
	 */
	limpiarDocumento() {
		this.documentoSeleccionado = undefined;
		this.documentoUrl = undefined;
		this.mostrarModal = false;

		if (this.fileInput) {
			this.fileInput.nativeElement.value = "";
		}

		// Avisar al padre que ya no hay documento
		this.retornarDocumento.emit(undefined);
		this.urlTemporal.emit(undefined);
	}
}
